import * as React from 'react';
import { useMemo } from 'react';
import cn from 'classnames';

import { CardExtraPrice, ServiceType } from '../../types';
import { ThemeProvider, useTheme } from '../../../theme';
import { ServicePopupModel, useCardExtraModal } from './cardExtraModal.hook';
import { getUserInfo } from '../../../Checkout/utils';
import { useConfigRedirects } from '../../../hooks';
import { useCardExtraCtx } from '../CardExtra.context';
import { useRenderers } from '../../../renderProps';
import { UpgradeFlightClass } from '../../../UpgradeFlightClass';
import { ModalX } from '../../../BaseComponents';
import ServiceCardDialog from '../../../Checkout/components/Checkout/OrderPaidServiceCards/components/ServiceCardDialog/ServiceCardDialog';
import ServicePage from '../../../Checkout/components/Checkout/ServicePage/ServicePage';
import PetPopup from '../../../CheckoutAdditionalService/components/PetPopup/PetPopup';
import AeroexpressPopup from '../../../CheckoutAdditionalService/components/AeroexpressPopup/AeroexpressPopup';
import InsurancePopup from '../../../InsurancePopup';
import VipServiceSelect from '../../../VipServiceSelect';
import LoyaltyConnect from '../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import SmsPopup from '../../../CheckoutAdditionalService/components/SmsNotifications/components/SmsPopup';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../utils';
import ModalFullscreen from '../../../ModalFullscreen';
import { MobileDialog } from '@shared/ui';
import FullscreenModalHeader from '../../../BaseComponents/Header/components/FullscreenModalHeader';

export interface Props {
	className?: string;
	openServiceType?: ServiceType;
	passengerPrices: CardExtraPrice[];
	isOpenModal: boolean;
	isDifferentPrices?: boolean;
	onClose: () => void;
}

type ServiceModalType = React.FC<ServicePopupModel>;

const CardExtraModal: React.FC<Props> = props => {
	const { isOpenModal, onClose } = props;
	const { CardExtraModal: theme } = useTheme('CardExtra');

	const { type, userInfo, isLoading, updateUser } = useCardExtraCtx();
	const model = useCardExtraModal(props);

	const { t } = useTranslation('Checkout');
	const isMobile = useIsMobile();

	const { CheckoutUpgradeFlightClass: UpgradeFlightClassModal = UpgradeFlightClass } = useRenderers();

	const { onGoToAccountSettings } = useConfigRedirects();

	const ServiceModal = useMemo<ServiceModalType>(() => {
		switch (type) {
			case 'animal':
				return PetPopup as ServiceModalType;
			case 'transfer':
				return AeroexpressPopup as ServiceModalType;
			case 'insurance':
				return InsurancePopup as ServiceModalType;
			case 'business-lounge':
			case 'priority-boarding':
			case 'baggage-delivery':
			case 'capsule-hotel':
			case 'misc':
				return VipServiceSelect as ServiceModalType;
			case 'upgrade':
				return UpgradeFlightClassModal as ServiceModalType;
			case 'sms':
				return SmsPopup as ServiceModalType;
			default:
				return null;
		}
	}, [type]);

	const modalClassName = cn(theme.root, {
		[theme.root_animal]: type === 'animal',
		[theme.root_baggageDelivery]: type === 'baggage-delivery',
		[theme.root_businessLounge]: type === 'business-lounge',
		[theme.root_priorityBoarding]: type === 'priority-boarding',
		[theme.root_insurance]: type === 'insurance',
		[theme.root_transfer]: type === 'transfer',
		[theme.root_capsuleHotel]: type === 'capsule-hotel',
		[theme.root_loyalty]: type === 'loyalty',
		[theme.root_upgrade]: type === 'upgrade',
		[theme.root_sms]: type === 'sms',
		[theme.root_misc]: type === 'misc'
	});

	if (model.isFullscreenDialog) {
		// Full screen modal for seats, baggage and meal services
		return (
			<ServicePage>
				{childrenProps => <ServiceCardDialog {...model.serviceDialogModel} {...childrenProps} />}
			</ServicePage>
		);
	}

	if (type === 'loyalty') {
		const onUpdateUser = () => {
			updateUser?.();
			onClose();
		};

		return (
			<LoyaltyConnect
				isOpen={isOpenModal}
				onClose={onClose}
				authType="authenticate"
				userInfo={getUserInfo(userInfo)}
				onLoyaltyCardSaved={onUpdateUser}
				onGoToSettings={onGoToAccountSettings}
			/>
		);
	}

	if (!ServiceModal) {
		return null;
	}

	if (type === 'upgrade') {
		const content = (
			<ServiceModal
				{...{
					...model.servicePopupModel,
					isLoading
				}}
			/>
		);

		return !isMobile ? (
			<ModalFullscreen
				isOpen={isOpenModal}
				size="fullscreen"
				ignoreClose={false}
				keepMounted={false}
				title={
					<FullscreenModalHeader
						title={t('Upgrading your flight')}
						onClose={onClose}
						onClickBackward={onClose}
					/>
				}
			>
				{content}
			</ModalFullscreen>
		) : (
			<MobileDialog title={t('Upgrading your flight')} isOpen={isOpenModal} onClose={onClose}>
				{content}
			</MobileDialog>
		);
	}

	return (
		<ThemeProvider
			value={{
				BaseComponents: {
					ModalX: {
						closeButton: theme.modal__closeButton
					}
				},
				VipServiceSelect: {
					VipServiceSelect: {
						header: theme.header
					}
				}
			}}
		>
			<ModalX
				size={model.size}
				scroll={model.scroll}
				classes={{ root: modalClassName, top_wrapper: theme.content }}
				isOpen={isOpenModal}
				onClose={onClose}
				noPadding={true}
				keepMounted={false}
			>
				<ServiceModal
					{...{
						...model.servicePopupModel,
						isLoading: isLoading
					}}
				/>
			</ModalX>
		</ThemeProvider>
	);
};

export default CardExtraModal;
